// @ts-check

export const OPTIMIZED_SCROLL = 'optimizedScroll';
export const OPTIMIZED_RESIZE = 'optimizedResize';

/**
 * Keeps throttled events subjects
 * @type { { [eventKey: string]: Array<HTMLElement | Window> } }
 */
const throttledEventsMap = {};

/**
 * Uses requestAnimationFrame for preventing DOM overcall by expensive events
 * @param {string} eventName Event name to throttle
 * @param {string} throttledEventName Throttled event name
 * @param {HTMLElement | Window} [element=window] Event subject
 */
export const throttle = (eventName, throttledEventName, element = window) => {
  const eventKey = `${eventName}.${throttledEventName}`;

  if (throttledEventsMap[eventKey] === undefined) {
    throttledEventsMap[eventKey] = [];
  }

  // The event is already throttled for the element
  if (throttledEventsMap[eventKey].includes(element)) {
    return;
  }

  throttledEventsMap[eventKey].push(element);

  const $element = $(element);
  let running = false;

  const func = () => {
    if (running) {
      return;
    }

    running = true;

    requestAnimationFrame(() => {
      $element.trigger(throttledEventName);
      running = false;
    });
  };

  $element.on(eventName, func);
};

/**
 * @typedef {object} OptimizedEventListenerParams
 * @property {HTMLElement | Window} [container=window]
 * @property {() => void} callback
 */

/**
 * @param {string} eventName
 * @param {string} optimizedEventName
 */
function createAddEventListenerFunction(eventName, optimizedEventName) {
  /** @param {OptimizedEventListenerParams} params */
  return ({ container = window, callback }) => {
    throttle(eventName, optimizedEventName, container);

    const $container = $(container);

    $container.on(optimizedEventName, callback);

    return function removeEventListener() {
      $container.off(optimizedEventName, callback);
    };
  };
}

/**
 * @param {OptimizedEventListenerParams} params
 * @returns {() => void} Remove event listener function
 */
export const onOptimizedScroll = createAddEventListenerFunction('scroll', OPTIMIZED_SCROLL);

/**
 * @param {OptimizedEventListenerParams} params
 * @returns {() => void} Remove event listener function
 */
export const onOptimizedResize = createAddEventListenerFunction('resize', OPTIMIZED_RESIZE);
